/**
 * ScheduleUpdateModal - Configure schedule update settings.
 * Scheduler selects: update day/time (CST), responsible persons (multi-select + search),
 * frozen progress window, and notes.
 *
 * Email automation (2 days before the update day):
 *   - 7 AM CST  → Reminder to all responsible persons with portal link
 *   - 5 PM CST  → Excel export (with notes) sent to schedulers (Yousef & Behzad)
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import ScheduleModulesPanel from './ScheduleModulesPanel';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hh = String(h).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    TIME_OPTIONS.push({ value: `${hh}:${mm}`, label: `${h12}:${mm} ${ampm} CST` });
  }
}

const BTN = { padding: '8px 16px', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 };

const FILTER_TYPE_LABELS = {
  all: 'Whole schedule',
  wbs: 'WBS node',
  resource: 'Resource (trade)',
  upd_resp: 'P6 Upd Resp code',
  resp_party: 'Resp Party',
  prefix: 'Activity ID prefix'
};

const RECIPIENT_MODE_LABELS = {
  upd_resp: 'P6 Upd Resp (automatic)',
  manual: 'My list below',
  both: 'Both'
};

const newRecipientRow = () => ({ id: `r${Date.now().toString(36)}`, label: '', filterType: 'wbs', values: [], to: [] });

const ScheduleUpdateModal = ({ isOpen, onClose, jobNumber }) => {
  const { instance, accounts } = useMsal();
  const [settings, setSettings] = useState({
    submittal_day: 'Friday',
    submittal_time: '17:00',
    frozen_progress_day: 'Thursday',
    frozen_progress_time: '12:00',
    responsible_user_ids: [],
    scheduler_notes: '',
    updater_emails_enabled: false,
    updater_recipient_mode: 'upd_resp',
    updater_recipients: [],
    updater_extra_cc: []
  });
  const [extraCcText, setExtraCcText] = useState('');
  const [projectMembers, setProjectMembers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updaters, setUpdaters] = useState(null);
  const [updatersError, setUpdatersError] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState('');
  const userDropdownRef = useRef(null);

  const fetchSettings = useCallback(async () => {
    if (!jobNumber) return;
    try {
      setLoading(true);
      const [settingsRes, membersRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', `/schedule-baseline/${jobNumber}/update-settings`),
        authenticatedApiCall(instance, accounts, 'GET', `/schedule-baseline/${jobNumber}/project-members`)
      ]);
      const members = membersRes.data.success ? (membersRes.data.data || []) : [];
      setProjectMembers(members);

      if (settingsRes.data.success && settingsRes.data.data) {
        const d = settingsRes.data.data;
        const ids = Array.isArray(d.responsible_user_ids) ? d.responsible_user_ids : [];
        setSettings({
          submittal_day: d.submittal_day || 'Friday',
          submittal_time: d.submittal_time || '17:00',
          frozen_progress_day: d.frozen_progress_day || 'Thursday',
          frozen_progress_time: d.frozen_progress_time || '12:00',
          responsible_user_ids: ids,
          scheduler_notes: d.scheduler_notes || '',
          updater_emails_enabled: d.updater_emails_enabled === true,
          updater_recipient_mode: ['upd_resp', 'manual', 'both'].includes(d.updater_recipient_mode) ? d.updater_recipient_mode : 'upd_resp',
          updater_recipients: Array.isArray(d.updater_recipients) ? d.updater_recipients : [],
          updater_extra_cc: Array.isArray(d.updater_extra_cc) ? d.updater_extra_cc : []
        });
        setExtraCcText((Array.isArray(d.updater_extra_cc) ? d.updater_extra_cc : []).join(', '));
        const sel = ids.map(id => members.find(m => m.id === id)).filter(Boolean);
        if (sel.length === 0 && d.progress_responsible_user_id) {
          const legacy = members.find(m => m.id === d.progress_responsible_user_id);
          if (legacy) sel.push(legacy);
        }
        setSelectedUsers(sel);
      }
    } catch (err) {
      console.error('Error fetching update settings:', err);
    } finally { setLoading(false); }
  }, [instance, accounts, jobNumber]);

  const fetchUpdaters = useCallback(async () => {
    if (!jobNumber) return;
    try {
      setUpdatersError('');
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/schedule-baseline/${jobNumber}/updaters`);
      if (res.data.success) setUpdaters(res.data.data);
      else setUpdatersError(res.data.error || 'Could not resolve updaters');
    } catch (err) {
      console.error('Error fetching schedule updaters:', err);
      setUpdatersError('Could not resolve updaters');
    }
  }, [instance, accounts, jobNumber]);

  useEffect(() => { if (isOpen) { fetchSettings(); fetchUpdaters(); setTestResult(''); } }, [isOpen, fetchSettings, fetchUpdaters]);

  const handleSendTest = async () => {
    setSendingTest(true); setTestResult(''); setError('');
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', `/schedule-baseline/${jobNumber}/test-updater-emails`, {});
      if (res.data.success) {
        const d = res.data.data || {};
        setTestResult(`Test done — ${d.sent || 0} e-mail(s) sent to you, ${d.skipped || 0} skipped, ${d.failed || 0} failed.`);
      } else {
        setTestResult(res.data.error || 'Test failed');
      }
    } catch (err) {
      setTestResult(err.response?.data?.error || 'Test failed');
    } finally { setSendingTest(false); }
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filteredMembers = useMemo(() => {
    const selected = new Set(selectedUsers.map(u => u.id));
    const q = userSearch.toLowerCase();
    return projectMembers.filter(m =>
      !selected.has(m.id) &&
      ((m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q))
    );
  }, [projectMembers, selectedUsers, userSearch]);

  const addUser = (member) => {
    setSelectedUsers(prev => [...prev, member]);
    setSettings(prev => ({ ...prev, responsible_user_ids: [...prev.responsible_user_ids, member.id] }));
    setUserSearch('');
  };

  const removeUser = (userId) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
    setSettings(prev => ({ ...prev, responsible_user_ids: prev.responsible_user_ids.filter(id => id !== userId) }));
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const payload = {
        ...settings,
        updater_extra_cc: extraCcText.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean),
        progress_responsible_user_id: settings.responsible_user_ids[0] || null
      };
      const res = await authenticatedApiCall(instance, accounts, 'PUT', `/schedule-baseline/${jobNumber}/update-settings`, payload);
      if (res.data.success) {
        setSuccess('Settings saved. Automated reminder & export emails are active.');
        setTimeout(() => setSuccess(''), 4000);
        fetchUpdaters();
      } else {
        setError(res.data.error || 'Failed to save');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;

  const reminderDay = (() => {
    const idx = DAYS_OF_WEEK.indexOf(settings.submittal_day);
    if (idx === -1) return '?';
    return DAYS_OF_WEEK[(idx + 5) % 7];
  })();

  const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px', color: '#374151' };
  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' };

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: '12px', width: '600px', maxWidth: '92vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem' }}>📋 Schedule Update Settings</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
          {loading ? <p style={{ textAlign: 'center', color: '#6c757d' }}>Loading...</p> : (
            <>
              {/* Update Day & Time */}
              <div style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                <h4 style={{ margin: '0 0 0.75rem', color: '#0369a1', fontSize: '0.9rem' }}>Update Day & Time (CST)</h4>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Weekday</label>
                    <select value={settings.submittal_day} onChange={e => setSettings(p => ({ ...p, submittal_day: e.target.value }))} style={inputStyle}>
                      {DAYS_OF_WEEK.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Time (CST)</label>
                    <select value={settings.submittal_time} onChange={e => setSettings(p => ({ ...p, submittal_time: e.target.value }))} style={inputStyle}>
                      {TIME_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Frozen Progress Update */}
              <div style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                <h4 style={{ margin: '0 0 0.75rem', color: '#92400e', fontSize: '0.9rem' }}>Frozen Progress Update By</h4>
                <p style={{ fontSize: '0.75rem', color: '#78716c', margin: '0 0 0.5rem' }}>Progress, remaining duration, barriers, etc. in notes must be frozen by this date/time.</p>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Day</label>
                    <select value={settings.frozen_progress_day} onChange={e => setSettings(p => ({ ...p, frozen_progress_day: e.target.value }))} style={inputStyle}>
                      {DAYS_OF_WEEK.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Time (CST)</label>
                    <select value={settings.frozen_progress_time} onChange={e => setSettings(p => ({ ...p, frozen_progress_time: e.target.value }))} style={inputStyle}>
                      {TIME_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Responsible Persons (multi-select with search) */}
              <div style={{ marginBottom: '1.25rem' }} ref={userDropdownRef}>
                <label style={labelStyle}>Responsible for Update ({selectedUsers.length} selected)</label>
                <div style={{ border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px', minHeight: '42px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', backgroundColor: 'white' }}>
                  {selectedUsers.map(u => (
                    <span key={u.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', backgroundColor: '#dbeafe', color: '#1e40af', borderRadius: '4px', fontSize: '0.78rem', fontWeight: 500 }}>
                      {u.name || u.email}
                      <button onClick={() => removeUser(u.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#1e40af', padding: '0 2px', lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={userSearch}
                    onChange={e => { setUserSearch(e.target.value); setShowUserDropdown(true); }}
                    onFocus={() => setShowUserDropdown(true)}
                    placeholder={selectedUsers.length === 0 ? 'Search team members...' : 'Add more...'}
                    style={{ border: 'none', outline: 'none', flex: 1, minWidth: '120px', fontSize: '0.82rem', padding: '4px' }}
                  />
                </div>
                {showUserDropdown && filteredMembers.length > 0 && (
                  <div style={{ border: '1px solid #d1d5db', borderRadius: '6px', marginTop: '4px', maxHeight: '180px', overflowY: 'auto', backgroundColor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                    {filteredMembers.map(m => (
                      <div key={m.id} onClick={() => { addUser(m); setShowUserDropdown(false); }} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.82rem', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f9ff'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}>
                        <span style={{ fontWeight: 500 }}>{m.name || 'No Name'}</span>
                        <span style={{ color: '#6c757d', fontSize: '0.75rem' }}>{m.email}</span>
                      </div>
                    ))}
                  </div>
                )}
                {projectMembers.length === 0 && !loading && (
                  <p style={{ fontSize: '0.75rem', color: '#dc3545', margin: '4px 0 0' }}>No project members found. Assign users to this project first.</p>
                )}
              </div>

              {/* Friday updater e-mails */}
              <div style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#eef2ff', borderRadius: '8px', border: '1px solid #c7d2fe' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <h4 style={{ margin: 0, color: '#3730a3', fontSize: '0.9rem' }}>Friday 7 AM — progress update request</h4>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', fontWeight: 600, color: '#3730a3', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings.updater_emails_enabled}
                      onChange={e => setSettings(p => ({ ...p, updater_emails_enabled: e.target.checked }))}
                    />
                    {settings.updater_emails_enabled ? 'On' : 'Off'}
                  </label>
                </div>
                <p style={{ fontSize: '0.75rem', color: '#4b5563', margin: '0.5rem 0 0.75rem', lineHeight: 1.5 }}>
                  Every <strong>Friday 7:00 AM ET</strong> the portal e-mails each person below, on behalf of Project Controls,
                  asking for physical % complete, obstacles and date or sequence changes before the <strong>Sunday midnight</strong>
                  cut-off. Two PDFs are attached, both filtered to that person's slice and laid out as the WBS tree like P6: the
                  <strong> 3-week look-ahead</strong> counted from the Sunday-midnight cut-off (the new data date), with
                  write-in boxes, and the <strong>full schedule</strong>. The subject says how many activities fall in those three
                  weeks. The project's <strong>PM</strong> and <strong>Project Controls</strong> are in CC; replies go to Project Controls.
                  The e-mail does not mention the portal. Save to apply.
                </p>

                {/* Audience */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#3730a3', marginBottom: 4 }}>Who receives it</div>
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {Object.entries(RECIPIENT_MODE_LABELS).map(([mode, text]) => (
                      <label key={mode} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', color: '#1e293b', cursor: 'pointer' }}>
                        <input type="radio" name="updater_recipient_mode" value={mode}
                          checked={settings.updater_recipient_mode === mode}
                          onChange={() => setSettings(p => ({ ...p, updater_recipient_mode: mode }))} />
                        {text}
                      </label>
                    ))}
                  </div>
                  <p style={{ fontSize: '0.72rem', color: '#64748b', margin: '4px 0 0' }}>
                    <em>P6 Upd Resp</em> reads the Upd Resp activity field from the Monday-night sync (employee code or company code).
                    <em> My list</em> lets you name the people yourself and pick which part of the schedule each one gets — use it
                    while Upd Resp is not filled in yet, or for a PM who updates everything (one row, "Whole schedule").
                  </p>
                </div>

                {/* Manual rows */}
                {settings.updater_recipient_mode !== 'upd_resp' && (
                  <div style={{ marginBottom: '0.75rem', backgroundColor: 'white', border: '1px solid #e0e7ff', borderRadius: 6, padding: '0.6rem' }}>
                    {settings.updater_recipients.length === 0 && (
                      <p style={{ fontSize: '0.75rem', color: '#92400e', margin: '0 0 0.5rem' }}>No rows yet. Add one row per person (or per discipline).</p>
                    )}
                    {settings.updater_recipients.map((row, idx) => {
                      const options = (updaters?.filterOptions?.[row.filterType]) || [];
                      const update = (patch) => setSettings(p => ({ ...p, updater_recipients: p.updater_recipients.map((r, i) => i === idx ? { ...r, ...patch } : r) }));
                      const remove = () => setSettings(p => ({ ...p, updater_recipients: p.updater_recipients.filter((_, i) => i !== idx) }));
                      const toggleValue = (v) => update({ values: row.values.includes(v) ? row.values.filter(x => x !== v) : [...row.values, v] });
                      const toText = (row.to || []).join(', ');
                      return (
                        <div key={row.id || idx} style={{ borderTop: idx ? '1px solid #f1f5f9' : 'none', padding: '0.5rem 0' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr 2fr auto', gap: 6, alignItems: 'start' }}>
                            <input value={row.label} placeholder="Label (e.g. Piping)" onChange={e => update({ label: e.target.value })}
                              style={{ ...inputStyle, padding: '5px 8px', fontSize: '0.78rem' }} />
                            <select value={row.filterType} onChange={e => update({ filterType: e.target.value, values: [] })}
                              style={{ ...inputStyle, padding: '5px 8px', fontSize: '0.78rem' }}>
                              {Object.entries(FILTER_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                            <input value={toText} placeholder="To: e-mail(s), comma separated"
                              onChange={e => update({ to: e.target.value.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean) })}
                              list={`members-${jobNumber}`}
                              style={{ ...inputStyle, padding: '5px 8px', fontSize: '0.78rem' }} />
                            <button onClick={remove} title="Remove row" style={{ ...BTN, padding: '4px 8px', color: '#b91c1c' }}>✕</button>
                          </div>
                          {row.filterType !== 'all' && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                              {options.length === 0 && <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>No values of this type on the schedule.</span>}
                              {options.map(o => {
                                const on = row.values.includes(o.value);
                                return (
                                  <button key={o.value} type="button" onClick={() => toggleValue(o.value)}
                                    style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 12, cursor: 'pointer',
                                      border: on ? '1px solid #4f46e5' : '1px solid #cbd5e1',
                                      backgroundColor: on ? '#4f46e5' : 'white', color: on ? 'white' : '#334155' }}>
                                    {o.value} <span style={{ opacity: 0.7 }}>({o.count})</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <datalist id={`members-${jobNumber}`}>
                      {projectMembers.filter(m => m.email).map(m => <option key={m.id} value={m.email}>{m.name}</option>)}
                    </datalist>
                    <button type="button" onClick={() => setSettings(p => ({ ...p, updater_recipients: [...p.updater_recipients, newRecipientRow()] }))}
                      style={{ ...BTN, padding: '4px 10px', fontSize: '0.75rem', marginTop: 4 }}>+ Add row</button>
                  </div>
                )}

                {/* Extra CC */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ ...labelStyle, fontSize: '0.75rem', color: '#3730a3' }}>Extra CC (optional)</label>
                  <input value={extraCcText} onChange={e => setExtraCcText(e.target.value)} placeholder="e-mail(s), comma separated — added to the PM and Project Controls"
                    style={{ ...inputStyle, padding: '5px 8px', fontSize: '0.78rem' }} />
                  <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 3 }}>
                    PM in CC: {updaters?.pmEmails?.length ? updaters.pmEmails.join(', ') : <span style={{ color: '#b45309' }}>none assigned to this project — add the PM under Admin → Projects, or put them in Extra CC</span>}
                  </div>
                </div>

                {updatersError && <p style={{ fontSize: '0.75rem', color: '#dc2626', margin: '0 0 0.5rem' }}>{updatersError}</p>}
                {updaters && (!updaters.groups || updaters.groups.length === 0) && (
                  <p style={{ fontSize: '0.78rem', color: '#92400e', margin: '0 0 0.75rem' }}>
                    Nobody would receive this yet. Fill the "Upd Resp" activity field in P6, or switch to "My list" and add rows, then Save.
                  </p>
                )}
                {updaters && updaters.groups && updaters.groups.length > 0 && (
                  <div style={{ border: '1px solid #e0e7ff', borderRadius: 6, overflow: 'hidden', backgroundColor: 'white', marginBottom: '0.75rem' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8fafc', color: '#475569' }}>
                          <th style={{ textAlign: 'left', padding: '6px 8px' }}>Slice</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px' }}>Goes to</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px' }}>E-mail(s)</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px' }}>Next 3 wks / all</th>
                        </tr>
                      </thead>
                      <tbody>
                        {updaters.groups.map(g => (
                          <tr key={`${g.source}-${g.value}`} style={{ borderTop: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '6px 8px', fontWeight: 600 }}>
                              {g.value} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{g.source === 'manual' ? `(${FILTER_TYPE_LABELS[g.filterType] || g.filterType})` : '(Upd Resp)'}</span>
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              {g.kind === 'unresolved'
                                ? <span style={{ color: '#dc2626', fontWeight: 600 }}>⚠ Not set</span>
                                : <>{g.label} {g.source !== 'manual' && <span style={{ color: '#94a3b8' }}>({g.kind === 'employee' ? 'employee' : 'company'})</span>}</>}
                            </td>
                            <td style={{ padding: '6px 8px', color: g.kind === 'unresolved' ? '#b91c1c' : '#334155' }}>
                              {g.kind === 'unresolved' ? g.reason : (g.recipients || []).join(', ')}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {g.lookAheadActivities} / {g.totalActivities}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    onClick={handleSendTest}
                    disabled={sendingTest || !updaters || !updaters.groups || updaters.groups.length === 0}
                    style={{ ...BTN, backgroundColor: '#4f46e5', color: 'white', border: '1px solid #4f46e5', opacity: (sendingTest || !updaters || !updaters.groups || updaters.groups.length === 0) ? 0.6 : 1 }}
                    title="Sends every e-mail for this job to YOU only ([TEST] subject, no CC) so you can review it. Uses the saved settings. Works while the switch is still off."
                  >
                    {sendingTest ? 'Sending…' : '✉ Send me a test'}
                  </button>
                  <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Save first — the test uses what is saved.</span>
                  {testResult && <span style={{ fontSize: '0.75rem', color: testResult.startsWith('Test done') ? '#166534' : '#b91c1c' }}>{testResult}</span>}
                </div>
              </div>

              {/* Optional schedule views (LBS, …) */}
              <div style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 0.5rem', color: '#0f172a', fontSize: '0.9rem' }}>Optional schedule views</h4>
                <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '0 0 0.75rem' }}>
                  Possibilities for this job. Check to show in the Schedule menu (best for linear / location-based work).
                </p>
                <ScheduleModulesPanel scopeType="job" scopeKey={jobNumber} compact />
              </div>

              {/* Notes */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Scheduler Notes</label>
                <textarea value={settings.scheduler_notes} onChange={e => setSettings(p => ({ ...p, scheduler_notes: e.target.value }))} placeholder="Additional notes or instructions..." style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} />
              </div>

              {/* Automated Email Info */}
              <div style={{ padding: '0.75rem', backgroundColor: '#e8f5e9', borderRadius: '6px', border: '1px solid #a5d6a7', fontSize: '0.75rem', color: '#2e7d32' }}>
                <strong>Automated Emails (2 days before {settings.submittal_day} = {reminderDay}):</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: '16px' }}>
                  <li><strong>7:00 AM CST on {reminderDay}</strong> — Reminder email sent to {selectedUsers.length > 0 ? selectedUsers.map(u => u.name || u.email).join(', ') : 'responsible person(s)'} with a link to the portal.</li>
                  <li><strong>5:00 PM CST on {reminderDay}</strong> — Exported Excel (with all notes) sent to schedulers (Yousef & Behzad).</li>
                  {settings.updater_emails_enabled && (
                    <li><strong>Friday 7:00 AM ET</strong> — Progress update request: each person on the list gets two PDFs filtered to their slice (3-week look-ahead + full schedule), from the portal for Project Controls, CC the project PM and Project Controls.</li>
                  )}
                </ul>
              </div>
            </>
          )}
        </div>

        {error && <div style={{ padding: '0.5rem 1.5rem', backgroundColor: '#f8d7da', color: '#721c24', fontSize: '0.8rem' }}>{error}</div>}
        {success && <div style={{ padding: '0.5rem 1.5rem', backgroundColor: '#d4edda', color: '#155724', fontSize: '0.8rem' }}>{success}</div>}

        <div style={{ padding: '0.75rem 1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} style={BTN}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ ...BTN, backgroundColor: '#28a745', color: 'white', border: '1px solid #28a745', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ScheduleUpdateModal;
